import React, { useState } from 'react'
import { Avatar, Dropdown, Table } from 'antd'
import type { MenuInfo } from 'rc-menu/lib/interface'
import {
  convertDOBToAge,
  convertToLocalWithAt,
  getNameSignature,
} from 'helper/utility'
import {
  CaretDown,
  CaretUp,
  CheckSquare,
  PencilSimpleLine,
  SignOut,
} from '@phosphor-icons/react'
import { PaginatedResp } from 'types/globalTypes'
import {
  Attendee,
  AttendeesTableRecord,
  CheckinOutAttendeeWithType,
  EventAttendeesListingResp,
  GetEventAttendeesParams,
} from 'types/event/eventAttendance'
import { history } from 'Redux/app-store'
import usePagination from 'helper/usePagination'
import { infoMessage } from 'api/global'
import { eventAttendanceStore } from 'state/event/eventAttendance'

interface AttendeesTableProps {
  data: PaginatedResp<EventAttendeesListingResp>
  params: GetEventAttendeesParams
  setParams: React.Dispatch<React.SetStateAction<GetEventAttendeesParams>>
}

const AttendeesTable = ({ data, params, setParams }: AttendeesTableProps) => {
  const { setCheckOutModalOpen, setParticipantData } = eventAttendanceStore(
    store => ({
      setParticipantData: store.setParticipantData,
      setCheckOutModalOpen: store.setCheckOutModalOpen,
    }),
  )
  const [showMoreId, setShowMoreID] = useState<string[]>([])
  const [paginationInfo, onTableChange] = usePagination({
    totalCount: data?.count || 0,
    currentPage: params.page,
    setParams: setParams,
  })

  const hanldeNavigaiton = (
    id: string,
    type: 'kid' | 'authorizedAdult' | 'guardian',
  ) => {
    if (type === 'kid') {
      history.push({
        pathname: `/family/kid-profile/${id}`,
      })
    } else if (type === 'authorizedAdult') {
      history.push({
        pathname: `/family/adult-profile/${id}`,
      })
    } else if (type === 'guardian') {
      history.push({
        pathname: `/family/parent-profile/${id}`,
      })
    }
  }

  const renderTicketNames = (tickets: string[], index: string) => {
    const showAllTickets = showMoreId.includes(index)

    return (
      <div key={`ticket-${index}`}>
        {tickets.map((ticket, i) => (
          <h2 key={`ticket-${index}-${i}`} className="text-14">
            {showAllTickets || i === 0 ? ticket : null}
          </h2>
        ))}
        {tickets.length > 1 && (
          <h5
            style={{ display: 'flex', gap: '1rem', color: '#2BA7CA' }}
            className="cursor_pointer align-items-center"
            onClick={() => {
              if (showAllTickets) {
                setShowMoreID(showMoreId.filter((id: string) => id !== index))
              } else {
                setShowMoreID([...showMoreId, index])
              }
            }}
          >
            {showAllTickets ? 'Hide' : 'Show More'}
            {showAllTickets ? <CaretUp /> : <CaretDown />}
          </h5>
        )}
      </div>
    )
  }

  const rowSelection = {}

  const handleMenuClick = (
    e: MenuInfo,
    clickedRecord: CheckinOutAttendeeWithType | undefined,
  ) => {
    if (!clickedRecord) return infoMessage('Must check-in first')
    if (e.key === 'check-out') {
      console.log(clickedRecord, 'record')
      setParticipantData(clickedRecord)
      setCheckOutModalOpen(true)
    } else {
      infoMessage('Will be implemented soon')
    }
  }

  const getMenuItems = (
    checkInDetails: CheckinOutAttendeeWithType | undefined,
  ) => {
    const baseItems = [
      {
        key: 'edit',
        label: (
          <div className="flex align-center gap-16px">
            <PencilSimpleLine size={20} color="#000" />
            <p className="text-black">Edit Registration</p>
          </div>
        ),
      },
      {
        key: 'view',
        label: (
          <div className="flex align-center gap-16px">
            <CheckSquare size={20} color="#000" />
            <p className="text-black">View check-ins</p>
          </div>
        ),
      },
    ]

    if (checkInDetails) {
      baseItems.push({
        key: 'check-out',
        label: (
          <div className="flex align-center gap-16px">
            <SignOut size={20} color="#000" />
            <p className="text-black">Check-out</p>
          </div>
        ),
      })
    }

    return baseItems
  }

  const columns = [
    {
      title: 'Family',
      dataIndex: 'familyName',
      key: 'familyName',
    },
    {
      title: 'Attnendees',
      dataIndex: 'attendee',
      key: 'attendee',
      render: (text: Attendee) => (
        <div key={text._id} className="flex-center">
          <Avatar src={text?.photo?.thumb} size={32} alt={text.firstName}>
            {getNameSignature(text.firstName)}
          </Avatar>
          <h2
            className="text-14 flex ml-10px pointer"
            onClick={() => hanldeNavigaiton(text._id, text.type)}
          >
            {text.firstName + ' ' + text.lastName + ' '}
            {
              <span className="grey-txt text-12 w-500 pl-5px">
                {'(' + convertDOBToAge(text.dob) + '.y.o)'}
              </span>
            }
          </h2>
        </div>
      ),
    },
    {
      title: 'Ticket name',
      dataIndex: 'tickets',
      key: 'tickets',
      render: (text: string[], record: AttendeesTableRecord) => (
        <div key={record.key}>{renderTicketNames(text, record.key)}</div>
      ),
    },
    {
      title: 'Check-in date',
      dataIndex: 'checkinDate',
      key: 'checkinDate',
    },
    {
      title: 'Actions',
      width: '150px',
      dataIndex: 'action',
      key: 'action',
    },
  ]

  const tableData = data.records.map((item: EventAttendeesListingResp) => {
    const checkinDateDisplay =
      item.checkinDate === 'Not checked in yet'
        ? item.checkinDate
        : convertToLocalWithAt(item.checkinDate, 'DD MMM YYYY [at] h:mm A')
    return {
      key: item._id,
      familyName: item.familyName,
      attendee: item.attendee,
      tickets: item.tickets,
      checkinDate: checkinDateDisplay,
      action: (
        <Dropdown
          menu={{
            items: getMenuItems(item.checkInDetails),
            onClick: (e: any) => handleMenuClick(e, item.checkInDetails),
          }}
          placement="bottomRight"
          arrow={false}
          trigger={['click']}
        >
          <button className="bordered-prim-btn">Manage</button>
        </Dropdown>
      ),
    }
  })

  return (
    <div className="mt-20px">
      <Table
        className="colored_header_table"
        columns={columns}
        rowSelection={rowSelection}
        dataSource={tableData}
        pagination={
          data.hasNextPage || data.hasPrevPage
            ? { ...paginationInfo, pageSize: params.limit }
            : false
        }
        onChange={onTableChange}
      />
    </div>
  )
}

export default AttendeesTable
